import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Palette, Bell, Shield, CreditCard, Users, Globe, CheckCircle, AlertCircle, Upload, Image, Loader2, X, Briefcase } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

const Settings = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [customDomain, setCustomDomain] = useState('');
  const [requestStatus, setRequestStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Fetch current tenant data including logo
  useEffect(() => {
    const fetchTenantData = async () => {
      if (!user?.tenant_id) return;
      
      const { data: tenant } = await supabase
        .from('tenants')
        .select('logo_url, name')
        .eq('id', user.tenant_id)
        .single();
      
      if (tenant?.logo_url) {
        setCurrentLogo(tenant.logo_url);
      }
    };
    
    fetchTenantData();
  }, [user?.tenant_id]);

  const settingsSections = [
    { id: 'general', title: 'General', icon: Building2, description: 'School name, contact info, and basic settings' },
    { id: 'branding', title: 'Branding', icon: Palette, description: 'Colors, logo, and custom domain' },
    { id: 'notifications', title: 'Notifications', icon: Bell, description: 'Email and SMS notification preferences' },
    { id: 'security', title: 'Security', icon: Shield, description: 'Password policies and 2FA' },
    { id: 'billing', title: 'Billing', icon: CreditCard, description: 'Subscription and payment settings' },
    { id: 'team', title: 'Team Members', icon: Users, description: 'Manage admin and staff access' },
    { id: 'titles', title: 'Titles', icon: Briefcase, description: 'Job titles and relationship types' },
  ];

  const handleSectionClick = (sectionId) => {
    if (sectionId === 'titles') {
      navigate('titles');
    }
    // Other sections can be handled here as they're implemented
  };

  const handleCustomDomainRequest = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setRequestStatus(null);

    try {
      // Validate domain format
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(customDomain)) {
        throw new Error('Please enter a valid domain (e.g., portal.yourschool.com)');
      }

      // Get current tenant data to preserve existing settings
      const { data: tenant, error: fetchError } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', user?.tenant_id)
        .single();

      if (fetchError) throw fetchError;

      // Store the request in tenant settings (merge with existing settings)
      const existingSettings = tenant?.settings || {};
      const featureRequests = existingSettings.feature_requests || [];
      
      const { error: updateError } = await supabase
        .from('tenants')
        .update({
          settings: {
            ...existingSettings,
            feature_requests: [
              ...featureRequests,
              {
                type: 'custom_domain',
                domain: customDomain,
                requested_at: new Date().toISOString(),
                requested_by: user?.email,
                status: 'pending'
              }
            ]
          }
        })
        .eq('id', user?.tenant_id);

      if (updateError) throw updateError;

      setRequestStatus({ type: 'success', message: 'Thanks! We\'ve noted your interest in custom domains.' });
      setCustomDomain('');
    } catch (err) {
      console.error('Custom domain request error:', err);
      setRequestStatus({ type: 'error', message: err.message || 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Logo upload handlers
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setUploadError(null);
    setUploadSuccess(false);
    
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a valid image file (PNG, JPG, GIF, SVG, or WebP)');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !user?.tenant_id) return;
    
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    
    try {
      // Generate unique filename
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `${user.tenant_id}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('tenant-logos')
        .upload(filePath, logoFile, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('tenant-logos')
        .getPublicUrl(filePath);
      
      // Update tenant record with new logo URL
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ logo_url: publicUrl })
        .eq('id', user.tenant_id);
      
      if (updateError) throw updateError;
      
      setCurrentLogo(publicUrl);
      setUploadSuccess(true);
      setLogoFile(null);
      setLogoPreview(null);
      
    } catch (err) {
      console.error('Logo upload error:', err);
      setUploadError(err.message || 'Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const clearLogoSelection = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setUploadError(null);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your school settings and preferences.</p>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsSections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className="bg-white rounded-xl p-6 shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <section.icon className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{section.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{section.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* School Info Form */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">School Information</h2>
        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">School Name</label>
              <input type="text" className="form-input" defaultValue="Demo School" />
            </div>
            <div>
              <label className="form-label">Subdomain</label>
              <input type="text" className="form-input" defaultValue="demo" disabled />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Email</label>
              <input type="email" className="form-input" defaultValue="contact@demoschool.com" />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input type="tel" className="form-input" defaultValue="(555) 123-4567" />
            </div>
          </div>
          <div>
            <label className="form-label">Address</label>
            <textarea className="form-input" rows={3} defaultValue="123 Main Street&#10;Anytown, ST 12345"></textarea>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary min-h-[44px]">
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {/* Logo Upload */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Image className="text-purple-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">School Logo</h2>
            <p className="text-sm text-gray-500">Upload your school's branding</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600">
            <strong>Where your logo appears:</strong>
          </p>
          <ul className="text-sm text-gray-500 mt-2 space-y-1 list-disc list-inside">
            <li>Dashboard sidebar and header</li>
            <li>Your school's login page</li>
            <li>Parent and employee portals</li>
            <li>Email notifications and PDF exports</li>
          </ul>
        </div>

        {/* Current Logo Display */}
        {(currentLogo || logoPreview) && (
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Logo Preview</p>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 bg-white border-2 border-purple-100 rounded-xl flex items-center justify-center p-2">
                <img 
                  src={logoPreview || currentLogo} 
                  alt="School Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="text-sm text-gray-500">
                <p>Recommended size: 400x400px</p>
                <p>Max file size: 5MB</p>
                <p>Formats: PNG, JPG, SVG, WebP</p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Controls */}
        <div className="space-y-4">
          {!logoFile ? (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-200 rounded-xl bg-purple-50 hover:bg-purple-100 cursor-pointer transition-colors">
              <Upload className="text-purple-400 mb-2" size={32} />
              <span className="text-sm text-purple-600 font-medium">Click to upload logo</span>
              <span className="text-xs text-gray-400 mt-1">or drag and drop</span>
              <input 
                type="file" 
                accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                onChange={handleLogoChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogoUpload}
                disabled={isUploading}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    <span>Upload Logo</span>
                  </>
                )}
              </button>
              <button
                onClick={clearLogoSelection}
                disabled={isUploading}
                className="flex items-center space-x-2 px-4 py-2 min-h-[44px] text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
            </div>
          )}

          {/* Error/Success Messages */}
          {uploadError && (
            <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle size={20} />
              <span className="text-sm">{uploadError}</span>
            </div>
          )}
          {uploadSuccess && (
            <div className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <CheckCircle size={20} />
              <span className="text-sm">Logo uploaded successfully!</span>
            </div>
          )}
        </div>
      </div>

      {/* Branding Preview */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Branding Preview</h2>
        <div className="flex flex-wrap items-center gap-6">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-fuchsia-500 rounded-xl flex items-center justify-center overflow-hidden">
            {currentLogo ? (
              <img src={currentLogo} alt="Logo" className="w-full h-full object-contain p-2" />
            ) : (
              <span className="text-white font-bold text-2xl">DS</span>
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">Primary Color</p>
            <div className="flex items-center space-x-2 mt-1">
              <div className="w-6 h-6 bg-purple-600 rounded"></div>
              <span className="text-sm text-gray-500">#9333ea</span>
            </div>
          </div>
          <div>
            <p className="font-medium text-gray-900">Secondary Color</p>
            <div className="flex items-center space-x-2 mt-1">
              <div className="w-6 h-6 bg-fuchsia-500 rounded"></div>
              <span className="text-sm text-gray-500">#d946ef</span>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Domain Request */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Globe className="text-blue-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Custom Domain</h2>
            <p className="text-sm text-gray-500">Use your own domain for your school portal</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600">
            <strong>Current URL:</strong> Your portal is currently accessible at{' '}
            <code className="bg-gray-200 px-2 py-1 rounded text-purple-600">
              https://[school-name].classroomdragon.com
            </code>
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-700">
            <strong>Coming Soon:</strong> Custom domains are not yet available, but we're tracking interest. 
            Let us know if you'd like this feature!
          </p>
        </div>

        <form onSubmit={handleCustomDomainRequest} className="space-y-4">
          <div>
            <label className="form-label">Request Custom Domain</label>
            <input
              type="text"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              placeholder="e.g., portal.yourschool.com"
              className="form-input"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the domain you'd like to use (e.g., portal.yourschool.com or app.yourschool.edu)
            </p>
          </div>

          {requestStatus && (
            <div className={`flex items-center space-x-2 p-4 rounded-lg ${
              requestStatus.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {requestStatus.type === 'success' ? (
                <CheckCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span className="text-sm">{requestStatus.message}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 max-w-md">
              <strong>Note:</strong> Custom domains require DNS configuration and SSL certificate setup. 
              Our team will contact you with next steps after reviewing your request.
            </p>
            <button
              type="submit"
              disabled={isSubmitting || !customDomain}
              className="btn-primary min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Domain'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
